import React, { useState, useEffect, useRef, useCallback } from "react";
import { IoGitCommit } from "react-icons/io5";

const ReleaseNoteModal = ({
  initialVisibilities,
  onSave,
  onClose,
  showVisibility = true,
  fragments = [], // Add fragments prop to receive the updated fragments array
}) => {
  const [comment, setComment] = useState("");
  const [visibilities, setVisibilities] = useState(initialVisibilities || []);
  const [hoveredVisibility, setHoveredVisibility] = useState(null);
  const commentRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ comment: comment, visibility: visibilities });
  };

  const handleEscapeKey = useCallback(
    async (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  const handleVisibilityChange = (level) => {
    setVisibilities((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  useEffect(() => {
    document.addEventListener("keydown", handleEscapeKey);
    // Focus on the comment textarea when the component mounts
    if (commentRef.current) {
      commentRef.current.focus();
    }
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [handleEscapeKey]);

  const visibilityLevels = [
    { value: "on-demand", label: "On-Demand" },
    { value: "champions", label: "Champions" },
    { value: "presentation", label: "Presentation" },
  ];

  const getVisibilityDescription = (visibility) => {
    switch (visibility) {
      case "on-demand":
        return "This version will be used for On-Demand validation.";
      case "champions":
        return "This version will be used for Champion validation.";
      case "presentation":
        return "This version will be used for Presentation validation.";
      default:
        return "";
    }
  };

  const renderFragmentChanges = () => {
    if (!fragments || fragments.length === 0) {
      return null;
    }

    return (
      <div className="mb-4">
        <h3 className="text-white text-sm font-medium mb-2">
          Fragment Changes:
        </h3>
        <div className="max-h-[150px] overflow-y-auto bg-gray-700 rounded p-2">
          {fragments.map((fragment, index) => {
            let style = "text-white";
            let prefix = "";

            if (
              fragment.is_fragment_changed === 1 ||
              fragment.is_fragment_changed === 2
            ) {
              style = "text-green-400";
              prefix = "+ ";
            } else if (fragment.is_fragment_changed === -1) {
              style = "text-red-400";
              prefix = "- ";
            }

            return (
              <div key={index} className={`${style} text-xs mb-1`}>
                {prefix}
                {fragment.block_name}{" "}
                <span className="text-gray-400 ml-1">
                  {fragment.is_version_changed ? (
                    <>
                      v{fragment.previous_version} → v{fragment.version}
                    </>
                  ) : (
                    <>v{fragment.version}</>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto z-[8900]">
      <div className="bg-gray-800 p-6 rounded-lg w-[500px] m-4">
        <form onSubmit={handleSubmit}>
          <div className="relative mb-4">
            <textarea
              ref={commentRef}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full p-2 pl-10 bg-gray-700 text-white rounded"
              placeholder="Version comment"
              maxLength={500}
              rows={4}
              required
            />
            <div className="absolute top-3 left-3 text-gray-400">
              <IoGitCommit size={20} />
            </div>
          </div>

          {/* Fragment Changes Display */}
          {renderFragmentChanges()}

          {showVisibility && (
            <div className="mb-4">
              <div className="flex justify-between items-end space-x-2 mb-2">
                {visibilityLevels.map((level, index) => (
                  <div
                    key={level.value}
                    className="flex flex-col items-center w-full"
                  >
                    <button
                      type="button"
                      onClick={() => handleVisibilityChange(level.value)}
                      onMouseEnter={() => setHoveredVisibility(level.value)}
                      onMouseLeave={() => setHoveredVisibility(null)}
                      className={`w-full h-16 rounded-md transition-colors ${
                        visibilities.includes(level.value)
                          ? "bg-fuchsia-600 hover:bg-fuchsia-500"
                          : "bg-gray-500/10 hover:bg-fuchsia-950"
                      }`}
                    ></button>
                    <span className="text-sm mt-2 text-white">
                      {level.label}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-400 mt-2 h-[30px]">
                {getVisibilityDescription(hoveredVisibility)}
              </p>
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-600 text-white px-4 py-2 rounded mr-2 hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReleaseNoteModal;
